import React, { useEffect, useState } from "react";
import IsLogin from "../../../Common/IsLogin";
import { useLocation } from "react-router-dom";
import Http from "../../../Common/Http";
import Alert from "../../../Common/Alert";

const ProductWisePurchaseInReport = () => {
  const [reportData, setReportData] = useState([]);
  const location = useLocation();
  const [alert, setAlert] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overAllData, setOverAllData] = useState();

  // Format date helper function
  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  // Get today's date for both start and end dates
  const getTodayDates = () => {
    const today = new Date();
    return {
      todayDate: formatDate(today),
    };
  };

  // Initialize date range with today's date for both start and end
  const [dateRange, setDateRange] = useState(() => {
    const { todayDate } = getTodayDates();
    return {
      start_date: todayDate,
      end_date: todayDate,
    };
  });

  const fetchData = async () => {
    setLoading(true);

    try {
      const params = {
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
      };

      const res = await Http.get(`/date-wise-purchase-in-report`, {
        params,
      });
      setOverAllData(res?.data);
      const resRole = await Http.get(`/staff-type-check`);

      if (res.data && res.data.data) {
        // Group data by product_name
        const groupedData = res.data.data.reduce((acc, item) => {
          const productName = item.product_name;
          if (!acc[productName]) {
            acc[productName] = {
              product_name: productName,
              vendors: [],
              total_qty: 0,
              total_value: 0,
            };
          }
          acc[productName].vendors.push({
            vendor_name: item.vendor_name,
            total_grn_qty: parseFloat(item.total_grn_qty),
            total_purchase_value: parseFloat(item.total_purchase_value),
          });
          acc[productName].total_qty += parseFloat(item.total_grn_qty);
          acc[productName].total_value += parseFloat(item.total_purchase_value);
          return acc;
        }, {});

        setReportData(Object.values(groupedData));
      } else {
        setReportData([]);
        showAlert("No data available for the selected date range", "warning");
      }

      setRole(resRole.data);
    } catch (error) {
      console.error("Fetch error:", error);
      showAlert(
        "Failed to fetch data: " +
        (error.response?.data?.message || error.message),
        "danger",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;

    setDateRange((prev) => {
      if (name === "end_date" && value < prev.start_date) {
        showAlert("End date cannot be earlier than start date", "warning");
        return prev;
      }

      if (name === "start_date" && value > prev.end_date) {
        return {
          start_date: value,
          end_date: value,
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchData();
  };

  const showAlert = (message, type) => {
    setAlert({
      message: message,
      type: type,
    });
    setTimeout(() => {
      setAlert(null);
    }, 4000);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  return (
    <div>
      <IsLogin></IsLogin>
      <div>
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            <h5 className="am-title">Product Wise Purchase / In Report</h5>
          </div>

          <div className="am-pagebody">
            <Alert alert={alert} />

            <div className="card pd-20 pd-sm-40">
              {/* Date Filter Form */}
              <div className="mb-4">
                <form
                  onSubmit={handleFilterSubmit}
                  className="row g-3 align-items-end"
                >
                  <div className="col-md-4">
                    <label htmlFor="start_date" className="form-label">
                      Start Date
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="start_date"
                      name="start_date"
                      value={dateRange.start_date}
                      onChange={handleDateChange}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="end_date" className="form-label">
                      End Date
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="end_date"
                      name="end_date"
                      value={dateRange.end_date}
                      onChange={handleDateChange}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <button type="submit" className="btn btn-primary">
                      Apply Filter
                    </button>
                  </div>
                </form>
              </div>

              {/* Custom Table for Grouped Data */}
              {loading ? (
                <p>Loading...</p>
              ) : reportData.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-bordered table-hover">
                    <thead>
                      <tr>
                        <th className="text-center">Product Name</th>
                        <th className="text-center">Vendor Name</th>
                        <th className="text-center">Total Purchase Quantity</th>
                        <th className="text-center">Total Purchase Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.map((productGroup, prodIndex) => (
                        <React.Fragment key={prodIndex}>
                          {productGroup.vendors.map((department, deptIndex) => (
                            <tr key={`${prodIndex}-${deptIndex}`}>
                              {deptIndex === 0 && (
                                <td
                                  rowSpan={productGroup.vendors.length}
                                  className="align-middle text-center font-weight-bold"
                                >
                                  {productGroup.product_name}
                                </td>
                              )}
                              <td>{department.vendor_name}</td>
                              <td className="text-center">
                                {department.total_grn_qty.toFixed(2)}
                              </td>
                              <td className="text-center">
                                {department.total_purchase_value.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                          <tr className="table-secondary font-weight-bold">
                            <td colSpan="2" className="text-right">
                              <strong>
                                Total for {productGroup.product_name}:
                              </strong>
                            </td>
                            <td className="text-center">
                              <strong>
                                {productGroup.total_qty.toFixed(2)}
                              </strong>
                            </td>
                            <td className="text-center">
                              <strong>
                                {productGroup.total_value.toFixed(2)}
                              </strong>
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="table-dark font-weight-bold">
                        <td colSpan="3" className="text-right">
                          <strong>Grand Total:</strong>
                        </td>
                        <td className="text-center">
                          <strong>{overAllData?.grand_total_purchase_value}</strong>
                        </td>
                      </tr>
                      <tr className="table-dark font-weight-bold">
                        <td colSpan="3" className="text-right">
                          <strong>Other Expense:</strong>
                        </td>
                        <td className="text-center">
                          <strong>{overAllData?.grand_total_expense}</strong>
                        </td>
                      </tr>
                      <tr className="table-dark font-weight-bold">
                        <td colSpan="3" className="text-right">
                          <strong>Other Discount:</strong>
                        </td>
                        <td className="text-center">
                          <strong>{overAllData?.grand_total_discount}</strong>
                        </td>
                      </tr>
                      <tr className="table-dark font-weight-bold">
                        <td colSpan="3" className="text-right">
                          <strong>Total:</strong>
                        </td>
                        <td className="text-center">
                          <strong>
                            {(
                              Number(overAllData?.grand_total_purchase_value || 0) +
                              Number(overAllData?.grand_total_expense || 0) -
                              Number(overAllData?.grand_total_discount || 0)
                            ).toFixed(2)}
                          </strong>                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p>No data available for the selected date range.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductWisePurchaseInReport;
